export const createSWRAbortError = () => Object.assign(new Error('Calcolo interrotto. Puoi avviarlo di nuovo.'), { name: 'AbortError' });
export const isSWRAbortError = error => error?.name === 'AbortError';

// Without Worker, stop at existing progress checkpoints. Synchronous work between
// checkpoints must finish first; no extra financial work is scheduled afterwards.
export async function runSWRDirectJob(run, onProgress = () => {}, execution = {}) {
  const check = () => { if (execution.signal?.aborted) throw createSWRAbortError(); };
  check();
  const result = await run(progress => { check(); onProgress?.(progress); check(); });
  check();
  return result;
}

// Each running job owns its worker: cancelling one cannot kill an independent export.
// A small queue bounds concurrent heaps. Numerical work inside each job is unchanged.
export function createSWRJobManager({ createWorker, maxConcurrent = 2, onActivity = () => {} }) {
  let nextId = 1;
  let active = 0;
  const jobs = new Map();
  const queue = [];
  const priority = type => type === 'run-swr-simulation' ? 0 : type.startsWith('generate-') ? 1 : 2;
  const listeners = new Set();
  const snapshot = () => [...jobs.values()].map(job => ({ id: job.id, activityId: job.activityId, type: job.type,
    status: job.worker ? 'running' : 'queued', queuePosition: job.worker ? null : queue.indexOf(job) + 1 }));
  const emit = (job, status) => {
    const event = { id: job.id, activityId: job.activityId, type: job.type, status, active, completeExternally: job.completeExternally };
    for (const listener of [onActivity, ...listeners]) { try { listener(event); } catch (_) { /* Observers cannot alter job execution. */ } }
  };

  const finish = (job, error, result) => {
    if (!jobs.has(job.id)) return;
    jobs.delete(job.id);
    job.signal?.removeEventListener('abort', job.abort);
    if (job.worker) {
      job.worker.onmessage = null;
      job.worker.onerror = null;
      job.worker.terminate();
      active--;
    }
    const index = queue.indexOf(job);
    if (index >= 0) queue.splice(index, 1);
    if (error && job.exportManifest && !error.exportManifest) {
      error.exportManifest = job.exportBundle && job.exportManifest.complete ? job.exportManifest : { ...job.exportManifest, complete: false,
        status: isSWRAbortError(error) ? 'cancelled' : 'error',
        parts: job.exportManifest.parts.map(p => ({ ...p, delivery: p.delivery === 'pending' ? 'unconfirmed' : p.delivery })) };
    }
    if (error && job.exportBundle && !error.exportBundle) error.exportBundle = { ...job.exportBundle,
      complete: false, status: isSWRAbortError(error) ? 'cancelled' : 'error',
      datasets: job.exportBundle.datasets.map(d => d.datasetId === error.exportManifest?.datasetId ? error.exportManifest : d) };
    if (error) job.reject(error); else job.resolve(result);
    emit(job, error ? isSWRAbortError(error) ? 'cancelled' : 'error' : 'complete');
    drain();
  };

  const drain = () => {
    while (active < maxConcurrent && queue.length) {
      const job = queue.shift();
      if (!jobs.has(job.id)) continue;
      try {
        const worker = createWorker();
        if (!jobs.has(job.id)) { worker.terminate(); continue; }
        job.worker = worker;
        active++;
        emit(job, 'running');
        job.worker.onmessage = async event => {
          if (!jobs.has(job.id)) return;
          const { id, type, progress, result, csv, files, error, manifest, part, deliveryKey, exportManifest, exportBundle } = event.data || {};
          if (id !== job.id) return;
          try {
            if (type === 'progress') { job.onProgress?.(progress); return; }
            if (type === 'export-bundle-manifest') {
              job.exportBundle = manifest;
              await job.onBundleManifest?.(manifest);
              if (jobs.has(job.id)) job.worker.postMessage({ id, type: 'export-part-ack', deliveryKey, accepted: true });
              return;
            }
            if (type === 'export-manifest') {
              job.exportManifest = manifest;
              if (job.exportBundle) {
                if (job.exportBundle.exportId !== manifest.exportId || job.exportBundle.datasets.some(d => d.datasetId === manifest.datasetId)) throw new Error('Dataset duplicato o associato a un altro export.');
                job.exportBundle.datasets.push(manifest);
              }
              job.manifestReady = Promise.resolve(job.onManifest?.(manifest));
              await job.manifestReady;
              if (deliveryKey && jobs.has(job.id)) job.worker.postMessage({ id, type: 'export-part-ack', deliveryKey, accepted: true });
              return;
            }
            if (type === 'export-dataset-complete') {
              const current = job.exportManifest;
              if (!job.exportBundle || !current || current.datasetId !== manifest.datasetId || current.exportId !== manifest.exportId
                || current.writtenPaths !== manifest.expectedPaths || current.parts.length !== manifest.parts.length
                || !manifest.complete || manifest.parts.some((p, i) => p.sha256 !== current.parts[i].sha256 || p.name !== current.parts[i].name)) throw new Error('Il dataset esportato non risulta completo.');
              job.exportManifest = manifest;
              const index = job.exportBundle.datasets.findIndex(d => d.datasetId === manifest.datasetId);
              job.exportBundle.datasets[index] = manifest;
              await job.onDatasetComplete?.(manifest);
              if (jobs.has(job.id)) job.worker.postMessage({ id, type: 'export-part-ack', deliveryKey, accepted: true });
              return;
            }
            if (type === 'export-part') {
              const state = job.exportManifest;
              if (!job.onPart || !state || job.receivingPart || part.exportId !== state.exportId || part.datasetId !== state.datasetId
                || part.firstPath !== state.writtenPaths + 1 || part.index !== state.parts.length + 1) throw new Error('Sequenza delle parti export non valida.');
              job.receivingPart = true;
              const descriptor = { ...part }; delete descriptor.content;
              const saved = { ...descriptor, delivery: 'pending' };
              state.parts.push(saved);
              try {
                await job.manifestReady;
                if (!jobs.has(job.id)) return;
                await job.onPart(part);
                if (!jobs.has(job.id)) return;
                saved.delivery = 'saved'; state.writtenPaths += part.paths;
                job.worker.postMessage({ id, type: 'export-part-ack', deliveryKey, accepted: true });
              } catch (saveError) {
                if (!jobs.has(job.id)) return;
                saved.delivery = 'unconfirmed';
                job.worker.postMessage({ id, type: 'export-part-ack', deliveryKey, accepted: false, error: saveError.message });
              } finally { job.receivingPart = false; }
              return;
            }
            if (type === 'error') { finish(job, Object.assign(new Error(error || 'Il calcolo SWR non è riuscito. Riprova.'),
              exportManifest ? { exportManifest } : {}, exportBundle ? { exportBundle } : {})); return; }
            if (!['result', 'csv-result', 'stress-csv-result'].includes(type)) throw new Error('Risposta del worker SWR non riconosciuta.');
            finish(job, null, type === 'csv-result' ? csv : type === 'stress-csv-result' ? files || [] : result);
          } catch (responseError) { finish(job, responseError); }
        };
        job.worker.onerror = event => finish(job, new Error(event?.message || 'Il calcolo SWR non è riuscito. Riprova.'));
        job.worker.postMessage({ id: job.id, type: job.type, params: job.params, ...job.extra });
      } catch (error) { finish(job, error); }
    }
  };

  return {
    run(type, params, onProgress = () => {}, extra = {}, execution = {}) {
      if (execution.signal?.aborted) return Promise.reject(createSWRAbortError());
      return new Promise((resolve, reject) => {
        const job = { id: nextId++, type, params, onProgress, extra, resolve, reject, signal: execution.signal,
          onPart: execution.onPart, onManifest: execution.onManifest, onBundleManifest: execution.onBundleManifest,
          onDatasetComplete: execution.onDatasetComplete, activityId: execution.activityId, completeExternally: execution.completeExternally, worker: null };
        job.abort = () => finish(job, createSWRAbortError());
        jobs.set(job.id, job);
        job.signal?.addEventListener('abort', job.abort, { once: true });
        queue.push(job);
        queue.sort((a, b) => priority(a.type) - priority(b.type) || a.id - b.id);
        emit(job, 'queued');
        drain();
      });
    },
    inspect() { return { active, queued: queue.length, pending: jobs.size }; },
    snapshot,
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
  };
}
